/**
 * useTheme — Light/dark mode with system preference fallback.
 *
 * Theme values stored in localStorage under the key 'dcvfd-theme'.
 * - 'system': follow prefers-color-scheme (default)
 * - 'light':  always light
 * - 'dark':   always dark
 *
 * The resolved theme ('light' | 'dark') is applied as a class on <html>.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { createElement } from "react";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  /** The stored preference (system | light | dark) */
  theme: ThemePreference;
  /** The actual resolved theme after applying system preference */
  resolvedTheme: ResolvedTheme;
  /** Set the theme preference */
  setTheme: (t: ThemePreference) => void;
  /** Cycle: system → light → dark → system */
  cycleTheme: () => void;
}

const STORAGE_KEY = "dcvfd-theme";
const CYCLE_ORDER: ThemePreference[] = ["system", "light", "dark"];

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system")
      return stored;
  } catch {
    // localStorage not available (SSR / private mode edge case)
  }
  return "system";
}

function getSystemTheme(): ResolvedTheme {
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

function applyThemeClass(resolved: ResolvedTheme) {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const pref = readStoredTheme();
    return pref === "system" ? getSystemTheme() : pref;
  });

  // Apply class on mount and whenever resolved changes
  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  // Watch system preference changes when mode is 'system'
  useEffect(() => {
    if (theme !== "system") return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const next: ResolvedTheme = e.matches ? "dark" : "light";
      setResolvedTheme(next);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // best-effort
    }
    const resolved: ResolvedTheme = t === "system" ? getSystemTheme() : t;
    setResolvedTheme(resolved);
  }, []);

  const cycleTheme = useCallback(() => {
    const idx = CYCLE_ORDER.indexOf(theme);
    const next = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
    setTheme(next);
  }, [theme, setTheme]);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme,
    cycleTheme,
  };
  return createElement(ThemeContext.Provider, { value }, children);
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
